import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import Box from '@mui/material/Box';
import defaultImage from './../../assets/auth/defaultImage.png';
import { InputLabel, Typography, CircularProgress } from '@mui/material';
import { useTheme } from '@emotion/react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, Tooltip } from '@mui/material';
import i18next from 'i18next';
import { useUploadMedia } from '../../hooks/media/useUploadMedia';
import { useDeleteMedia } from '../../hooks/media/useDeleteMedia';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export default function InputFileUpload({
    labelImage,
    labelBioImage,
    width = 120,
    height,
    borderRadius = '50%',
    onChange,
    value,
    name = 'logo',
    required,
    // Note: isUploading, errorMessage, successMessage might be passed from parent
    // but we will also handle them internally via hooks
}) {
    const theme = useTheme();
    const { uploadMedia, isPending: isUploadingLocal } = useUploadMedia();
    const { deleteSingleMedia, isDeleting } = useDeleteMedia();

    const [localError, setLocalError] = React.useState('');
    const [localSuccess, setLocalSuccess] = React.useState('');

    // Determine the URL for display
    const getDisplayUrl = (val) => {
        if (!val) return defaultImage;
        if (typeof val === 'string') {
            // If it's a URL or Base64, show it
            if (val.startsWith('http') || val.startsWith('data:') || val.startsWith('/')) return val;
            // It might be a temp filename, but we usually have the local preview stored in imageUrl state
            return null;
        }
        if (typeof val === 'object') {
            return val.preview_url || val.url || val.preview || val.thumb_url || defaultImage;
        }
        return defaultImage;
    };

    const [imageUrl, setImageUrl] = React.useState(getDisplayUrl(value) || defaultImage);

    React.useEffect(() => {
        const potentialUrl = getDisplayUrl(value);
        if (potentialUrl && potentialUrl !== defaultImage) {
            setImageUrl(potentialUrl);
        } else if (value === null) {
            setImageUrl(defaultImage);
        }
    }, [value]);

    const handleFile = async (file) => {
        setLocalError('');
        setLocalSuccess('');

        // 1. Show local preview immediately
        const previewUrl = URL.createObjectURL(file);
        setImageUrl(previewUrl);

        // 2. Prepare FormData for upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'image');

        // 3. Trigger Upload
        uploadMedia(formData, {
            onSuccess: (res) => {
                const uploadedName = res?.data?.name;
                if (uploadedName) {
                    setLocalSuccess(i18next.language === 'ar' ? 'تم الرفع بنجاح' : 'Uploaded successfully');
                    if (onChange) {
                        onChange({
                            target: {
                                name,
                                value: uploadedName
                            }
                        });
                    }
                    // Keep the local preview (blob URL) active so the user sees the image
                }
            },
            onError: (error) => {
                setLocalError(error?.response?.data?.message || 'Upload failed');
                // Only revert if we don't have a value yet
                if (!value) {
                    setImageUrl(defaultImage);
                }
                URL.revokeObjectURL(previewUrl);
            }
        });
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        e.preventDefault();

        // If it's an existing image object with an ID, call the delete API
        if (typeof value === 'object' && value?.id) {
            deleteSingleMedia(value.id, {
                onSuccess: () => {
                    if (onChange) onChange({ target: { name, value: null } });
                    setImageUrl(defaultImage);
                }
            });
        } else {
            // It's a temp filename or a string, just clear it locally
            if (onChange) onChange({ target: { name, value: null } });
            setImageUrl(defaultImage);
        }
    };

    const onDrop = React.useCallback(
        (acceptedFiles) => {
            const file = acceptedFiles?.[0];
            if (file) handleFile(file);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [name, onChange]
    );

    // ✅ التعديل المهم هنا: إضافة noClick و noKeyboard
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'image/*': [] },
        multiple: false,
        noClick: true,     // ✅ منع فتح النافذة عند النقر على المنطقة
        noKeyboard: true,  // ✅ منع فتح النافذة بالكيبورد
    });

    let lng = i18next.language;

    return (
        <>
            <Box
                {...getRootProps()}
                component="label"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    width,
                    height,
                    borderRadius,
                    overflow: 'hidden',
                    backgroundColor: (t) => t.palette.background.file,
                    position: 'relative',
                    border: `1px dashed ${isDragActive
                        ? theme.palette.primary.main
                        : theme.palette.divider
                        }`,
                    transition: 'border-color 0.2s ease, background-color 0.2s ease',
                    '&:hover': {
                        backgroundColor: (t) =>
                            t.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.04)'
                                : 'rgba(0,0,0,0.02)',
                    },
                }}
            >
                <img
                    src={imageUrl || defaultImage}
                    alt="avatar"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'scale-down',
                        opacity: (isUploadingLocal || isDeleting) ? 0.5 : 1,
                        transition: 'opacity 0.2s ease',
                    }}
                />
                <VisuallyHiddenInput
                    {...getInputProps()}
                    name={name}
                    required={required && imageUrl === defaultImage}
                />

                {/* Delete Icon Button */}
                {value && imageUrl !== defaultImage && (
                    <Tooltip title={lng === 'ar' ? 'حذف الصورة' : 'Delete Image'}>
                        <IconButton
                            onClick={handleDelete}
                            disabled={isDeleting || isUploadingLocal}
                            sx={{
                                position: 'absolute',
                                top: 5,
                                right: 5,
                                bgcolor: 'rgba(255, 255, 255, 0.8)',
                                color: 'error.main',
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 1)',
                                    transform: 'scale(1.1)',
                                },
                                zIndex: 99,
                                width: 34,
                                height: 34,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                border: '1px solid rgba(0,0,0,0.05)'
                            }}
                            size="small"
                        >
                            <DeleteIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                )}

                {/* طبقة حالة السحب */}
                {isDragActive && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: 'rgba(0,0,0,0.35)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontSize: 12,
                            textAlign: 'center',
                            px: 1,
                        }}
                    >
                        {lng === 'ar' ? 'إفلت الصورة هنا للرفع' : 'Drop the image here to upload'}
                    </Box>
                )}

                {/* طبقة التحميل */}
                {(isUploadingLocal || isDeleting) && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: 'rgba(0,0,0,0.45)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            color: '#fff',
                            fontSize: 12,
                        }}
                    >
                        <CircularProgress size={26} thickness={4} color="inherit" />
                        <Typography variant="caption">
                            {isDeleting
                                ? (lng === 'ar' ? 'جاري الحذف...' : 'Deleting...')
                                : (lng === 'ar' ? 'جاري رفع الصورة...' : 'Uploading...')
                            }
                        </Typography>
                    </Box>
                )}

                {/* حالة الخطأ */}
                {localError && !isUploadingLocal && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 6,
                            right: 6,
                            bgcolor: 'rgba(244, 67, 54, 0.9)',
                            borderRadius: 999,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.2,
                            py: 0.2,
                        }}
                    >
                        <ErrorIcon sx={{ fontSize: 14, color: '#fff' }} />
                        <Typography
                            variant="caption"
                            sx={{ color: '#fff', maxWidth: 140 }}
                        >
                            {lng === 'ar' ? 'فشل الرفع' : 'Failed'}
                        </Typography>
                    </Box>
                )}

                {/* حالة النجاح */}
                {localSuccess && !isUploadingLocal && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 6,
                            right: 6,
                            bgcolor: 'rgba(46, 125, 50, 0.9)',
                            borderRadius: 999,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.2,
                            py: 0.2,
                        }}
                    >
                        <CheckCircleIcon sx={{ fontSize: 14, color: '#fff' }} />
                        <Typography
                            variant="caption"
                            sx={{ color: '#fff', maxWidth: 140 }}
                        >
                            {lng === 'ar' ? 'تم الرفع' : 'Successful'}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box mt={1}>
                <InputLabel
                    htmlFor={name}
                    sx={{
                        mt: 1,
                        fontWeight: 'bold',
                        color: theme.palette.primary.default,
                    }}
                >
                    {labelImage}
                </InputLabel>
                {labelBioImage && (
                    <Typography
                        sx={{ fontWeight: 'bold', color: '#888' }}
                        variant="caption"
                    >
                        {labelBioImage}
                    </Typography>
                )}
            </Box>

            {/* رسائل الخطأ/النجاح */}
            {localError && (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: 'block' }}
                >
                    {localError}
                </Typography>
            )}
            {localSuccess && (
                <Typography
                    variant="caption"
                    color="success.main"
                    sx={{ mt: 0.5, display: 'block' }}
                >
                    {localSuccess}
                </Typography>
            )}
        </>
    );
}
